import { api, history, login, reducerUtil } from 'base-client';

import { accountUrl } from 'userAccount/utils';
import { actions as errorsActions } from 'errors';
import { actions as notificationActions, projectNotification } from 'notification';
import { sectors } from 'utils/staticData';
import accountData from 'userAccount/reducerData';
import tenantData from 'tenant/reducerData';
import { v4 as uuid } from 'uuid';

const toAccount = () => (dispatch, getState) => {
    // Check if we're in the middle of a login process
    const token = reducerUtil.getSlice(login.reducerData, login.reducerData.token, getState());
    const lsToken = localStorage.getItem('token');
    
    if (!token && !lsToken) {
        console.warn('No token found, skipping account redirect');
        return;
    }
    
    // Add a small delay to ensure the current navigation completes
    setTimeout(() => {
        history.push(accountUrl);
    }, 100);
};

const getUserAccount = (refresh) => async (dispatch, getState) => {
    const token = reducerUtil.getSlice(login.reducerData, login.reducerData.token, getState());

    if (!token) return null;
    if (!refresh) dispatch(reducerUtil.setSlice(accountData, accountData.data, undefined));

    try {
        const result = await dispatch(api.actions.get('users/my/profile'));
        
        if (!result) {
            console.warn('No user profile data received from API');
            return null;
        }
        
        const { firstName, lastName, occupation, industry, sector, profile } = result || {};
        const { companyName, phoneNumber, email } = profile || {};
        const account = {
            firstName,
            lastName,
            occupation,
            industry,
            companyName,
            phoneNumber,
            email,
            sector: sector || sectors[sectors.length - 1],
        };
        
        dispatch(reducerUtil.setSlice(accountData, accountData.data, account));
        return account;
    } catch (error) {
        console.error('Error fetching user account:', error);
        dispatch(errorsActions.error(error));
        return null;
    }
};

const setUserAccount = (values) => async (dispatch) => {
    try {
        const notification = [
            {
                id: `account-update-${uuid()}`,
                type: projectNotification.GENERAL,
                title: 'Account Updated',
                body: 'Your account data has been updated successfully.',
            },
        ];
        await dispatch(api.actions.patch('users/my/profile', JSON.stringify(values)));
        dispatch(notificationActions.sendNotifications(notification));
        dispatch(getUserAccount(true));
    } catch (error) {
        const notification = [
            {
                id: `account-update-${uuid()}`,
                type: projectNotification.GENERAL,
                title: 'Account Not Updated',
                body: 'Your account data has failed to update.',
            },
        ];
        dispatch(notificationActions.sendNotifications(notification));
        dispatch(errorsActions.error(error));
    }
};

const fillAccount = () => async (dispatch, getState) => {
    const token = reducerUtil.getSlice(login.reducerData, login.reducerData.token, getState());
    if (!token) {
        console.warn('No token found in fillAccount, skipping');
        return;
    }

    try {
        // Wait for the API call to complete and get the result
        const result = await dispatch(getUserAccount());
        
        // Add a small delay to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Get the updated account info from state
        const accountInfo = reducerUtil.getSlice(accountData, accountData.data, getState()) || {};
        
        // Only redirect if we have a valid account info object and it's missing required fields
        if (accountInfo && typeof accountInfo === 'object' && 
            (!accountInfo.companyName || !accountInfo.industry || !accountInfo.occupation)) {
            console.log('Account incomplete, redirecting to account page');
            dispatch(toAccount());
        } else {
            console.log('Account complete or no account data, continuing');
        }
    } catch (error) {
        // Log the error but don't redirect on API failure
        console.error('Error filling account:', error);
        dispatch(errorsActions.error(error));
        // Don't redirect on error - let the user continue with their current flow
    }
};

const clearAccount = () => (dispatch) => dispatch(reducerUtil.setSlice(accountData, accountData.data, undefined));

const deleteAccount = (values) => async (dispatch, getState) => {
    try {
        const tenant_id = reducerUtil.getSlice(tenantData, tenantData.tenant, getState());
        const result = await dispatch(api.actions.post('users/user-gdpr-delete', { ...values, tenant_id }));
        if (!result || result !== 'Email has been sent') {
            const notification = [
                {
                    id: `gdpr-deletion-req-${uuid()}`,
                    type: projectNotification.GENERAL,
                    title: 'Error',
                    body: 'Your data could not be deleted, please contact the company manually.',
                },
            ];
            dispatch(notificationActions.sendNotifications(notification));
        } else {
            clearAccount();
            dispatch(login.actions.logOut());
        }
        return result;
    } catch (error) {
        dispatch(errorsActions.error(error));
        return false;
    }
};

export default {
    toAccount,
    getUserAccount,
    setUserAccount,
    fillAccount,
    clearAccount,
    deleteAccount,
};
